import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, MessageCircle, Star, StarHalf, StarOff, Mail, Phone } from "lucide-react";
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx";
import styles from "./SendContactModal.module.scss";
import { sendContactNotification, closeSendContactNotificationModal } from "../../../store/actions";
import useUserImage from "../../../hooks/useUserImage";

type RootState = any;

const buildDefaultMessage = (name: string, email: string | null, phone: string | null) => {
  const contactLine = phone ? `Contacto: ${email ?? ""} - ${phone}` : `Contacto: ${email ?? ""}`;
  return `Hola ${name}! Me gustaría contactarte para hablar sobre tu perfil en Edify. ¿Podríamos agendar una reunión?\n${contactLine}`;
};

const buildPrivateProjectDefaultMessage = (
  name: string,
  projectName: string,
  email: string | null,
  phone: string | null
) => {
  const contactLine = phone ? `Contacto: ${email ?? ""} - ${phone}` : `Contacto: ${email ?? ""}`;
  return `Hola ${name}! Vi tu proyecto privado "${projectName}" en Edify y me gustaría contactarte al respecto. ¿Podemos coordinar?\n${contactLine}`;
};

const buildStars = (value: number | null | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value) || value <= 0) {
    return { displayValue: null, items: [] as Array<"full" | "half" | "empty"> };
  }
  const normalized = Math.max(0, Math.min(5, value));
  const integer = Math.floor(normalized);
  const hasHalf = normalized - integer >= 0.5;
  const items: Array<"full" | "half" | "empty"> = [];
  for (let index = 0; index < 5; index += 1) {
    if (index < integer) {
      items.push("full");
    } else if (index === integer && hasHalf) {
      items.push("half");
    } else {
      items.push("empty");
    }
  }
  return { displayValue: normalized.toFixed(normalized % 1 === 0 ? 0 : 1), items };
};

const SendContactModal: React.FC = () => {
  const dispatch = useDispatch<any>();
  const modalState = useSelector((state: RootState) => state.general?.showSendContactNotificationModal);
  const senderData = useSelector((state: RootState) => state.general?.userData);
  const isOpen = Boolean(modalState?.isOpen && modalState?.userData);
  const recipient = modalState?.userData;
  const relatedPrivateProjectName = modalState?.relatedPrivateProjectName as string | undefined;

  const recipientName = useMemo(() => {
    if (!recipient) return "";
    if (recipient.type === "COMPANY") {
      return recipient.razon_social ?? "Empresa";
    }
    const first = recipient.name ?? "";
    const last = recipient.last_name ?? "";
    const full = `${first} ${last}`.trim();
    return full.length > 0 ? full : recipient.username ?? "Profesional";
  }, [recipient]);

  const senderEmail = senderData?.username ?? null;
  const senderPhone = senderData?.phone ?? null;

  const initialMessage = useMemo(() => {
    const projectTitle = typeof relatedPrivateProjectName === "string" ? relatedPrivateProjectName.trim() : "";
    if (projectTitle.length > 0) {
      return buildPrivateProjectDefaultMessage(recipientName, projectTitle, senderEmail, senderPhone);
    }
    return buildDefaultMessage(recipientName, senderEmail, senderPhone);
  }, [relatedPrivateProjectName, recipientName, senderEmail, senderPhone]);
  const [message, setMessage] = useState(initialMessage);

  const { imageError, initials, profileImage } = useUserImage(recipient);
  const ratingData = useMemo(() => buildStars(recipient?.stars), [recipient?.stars]);

  const handleClose = useCallback(() => {
    dispatch(closeSendContactNotificationModal());
  }, [dispatch]);

  const handleSubmit = useCallback(() => {
    if (!recipient?.id) {
      return;
    }
    const idAsNumber = Number(recipient.id);
    if (Number.isNaN(idAsNumber)) {
      return;
    }
    const payload = message.trim().length > 0 ? message : initialMessage;
    dispatch(sendContactNotification(idAsNumber, payload, handleClose));
  }, [dispatch, recipient, message, initialMessage, handleClose]);

  const handleResetMessage = useCallback(() => {
    setMessage(initialMessage);
  }, [initialMessage]);

  React.useEffect(() => {
    setMessage(initialMessage);
  }, [initialMessage, isOpen]);

  if (!isOpen || !recipient) {
    return null;
  }

  const hasEmail = Boolean(recipient.username);
  const hasPhone = Boolean(recipient.phone);

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleClose} zIndex={60}>
      <div className={styles.container} role="dialog" aria-modal="true">
        <header className={styles.header}>
          <div className={styles.identity}>
            <div className={styles.avatar}>
              {profileImage && !imageError ? (
                <img src={profileImage} alt={recipientName} />
              ) : initials ? (
                <span>{initials}</span>
              ) : (
                <span>{recipientName.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className={styles.identityInfo}>
              <span className={styles.caption}>Enviar mensaje a</span>
              <h2 className={styles.name}>{recipientName}</h2>
              {ratingData.displayValue && (
                <div className={styles.rating}>
                  <span className={styles.ratingValue}>{ratingData.displayValue}</span>
                  <div className={styles.ratingStars}>
                    {ratingData.items.map((kind, index) => {
                      if (kind === "full") return <Star key={index} size={14} />;
                      if (kind === "half") return <StarHalf key={index} size={14} />;
                      return <StarOff key={index} size={14} />;
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </header>
        <section className={styles.contactSummary}>
          {hasEmail && (
            <span className={styles.contactItem}>
              <Mail size={14} />
              {recipient.username}
            </span>
          )}
          {hasPhone && (
            <span className={styles.contactItem}>
              <Phone size={14} />
              {recipient.phone}
            </span>
          )}
        </section>
        <section className={styles.body}>
          <div className={styles.fieldHeader}>
            <div>
              <h3 className={styles.fieldTitle}>Mensaje</h3>
              <p className={styles.fieldHint}>Podés editar el mensaje antes de enviarlo.</p>
            </div>
            <button type="button" className={styles.resetButton} onClick={handleResetMessage}>
              Restaurar
            </button>
          </div>
          <textarea
            className={styles.textarea}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={initialMessage}
          />
        </section>
        <footer className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={handleClose}>
            Cancelar
          </button>
          <button type="button" className={styles.submitButton} onClick={handleSubmit}>
            <MessageCircle size={18} />
            Enviar mensaje
          </button>
        </footer>
      </div>
    </ModalOverlay>
  );
};

export default SendContactModal;

